import React, { useEffect, useState } from 'react';
import { Icons } from './Icon';
import { itemCountLabel, type OverwriteReview } from '../../utils/project-overwrite-review';
import { QUOTE_BRAIN_WROTE_WHILE_EDITING } from '../../utils/quote-brain-project';

type ProjectUpdateNoticeProps = {
    hasLocalEdits: boolean;
    saveRefused?: boolean;
    // The saved version holds the Quote Brain's quote and this tab has not loaded it (review round 2, W1 to W3). The one
    // action is to load it: Review and Replace would erase the Quote Brain's quote.
    quoteBrainWrote?: boolean;
    onReload: () => Promise<void> | void;
    // Called with the save token of the version the operator agreed to replace.
    onOverwrite?: (token: string) => Promise<void> | void;
    // Reads the saved version so the operator sees what Replace discards (F48).
    // Null or a failure means nothing may be replaced.
    describeOverwrite?: () => Promise<OverwriteReview | null>;
    onDismiss: () => void;
};

const UNREADABLE_SAVED_VERSION = 'QuoteMaker could not read the saved version, so nothing was replaced. Reload to see it.';

export default function ProjectUpdateNotice({
    hasLocalEdits,
    saveRefused = false,
    quoteBrainWrote = false,
    onReload,
    onOverwrite,
    describeOverwrite,
    onDismiss,
}: ProjectUpdateNoticeProps) {
    const [reviewing, setReviewing] = useState(false);
    const [resolving, setResolving] = useState<'reload' | 'describe' | 'overwrite' | null>(null);
    const [confirming, setConfirming] = useState<OverwriteReview | null>(null);
    const [problem, setProblem] = useState<string | null>(null);

    useEffect(() => {
        if (!hasLocalEdits) setReviewing(false);
    }, [hasLocalEdits]);

    const reload = async () => {
        if (resolving) return;
        setResolving('reload');
        try {
            await onReload();
        } finally {
            setResolving(null);
        }
    };

    // Replace never happens on the first click: the saved version is read and
    // described first, and the operator confirms against that description.
    const reviewOverwrite = async () => {
        if (resolving || !onOverwrite) return;
        setProblem(null);
        if (!describeOverwrite) {
            setProblem(UNREADABLE_SAVED_VERSION);
            return;
        }
        setResolving('describe');
        try {
            const review = await describeOverwrite();
            if (review) setConfirming(review);
            else setProblem(UNREADABLE_SAVED_VERSION);
        } catch {
            setProblem(UNREADABLE_SAVED_VERSION);
        } finally {
            setResolving(null);
        }
    };

    const overwrite = async () => {
        if (resolving || !onOverwrite || !confirming) return;
        setResolving('overwrite');
        try {
            await onOverwrite(confirming.token);
        } finally {
            setResolving(null);
            setConfirming(null);
        }
    };

    const needsReview = hasLocalEdits || saveRefused;

    if (quoteBrainWrote) {
        return (
            <aside
                className="qm-project-update-notice"
                role="status"
                aria-live="polite"
                aria-atomic="true"
                data-local-edits={hasLocalEdits ? 'true' : 'false'}
                data-save-refused={saveRefused ? 'true' : 'false'}
                data-quote-brain-wrote="true"
            >
                <span className="qm-project-update-notice__signal" aria-hidden="true">
                    <Icons.RefreshCw size={15} />
                </span>
                <div className="qm-project-update-notice__copy">
                    <strong>New quote from QuoteMaker</strong>
                    <span>{needsReview ? QUOTE_BRAIN_WROTE_WHILE_EDITING : 'QuoteMaker wrote this job\'s quote. Load it to see the quote.'}</span>
                </div>
                <div className="qm-project-update-notice__actions">
                    <button type="button" className="qm-project-update-notice__action" onClick={reload} disabled={resolving !== null}>
                        {resolving === 'reload' ? 'Loading…' : 'Load QuoteMaker\'s quote'}
                    </button>
                </div>
                <button
                    type="button"
                    className="qm-project-update-notice__dismiss"
                    onClick={onDismiss}
                    aria-label="Dismiss update notice"
                    title="Dismiss update notice"
                >
                    <Icons.X size={15} />
                </button>
            </aside>
        );
    }

    const title = confirming
        ? 'Replace the saved version?'
        : reviewing
            ? onOverwrite ? 'Choose which version to keep' : 'Choose the saved version'
            : saveRefused ? 'Saved version changed' : 'Updated elsewhere';
    const body = confirming
        ? `The saved version is from ${confirming.savedAtLabel}: ${itemCountLabel(confirming.savedItems)}, ${confirming.savedTotalLabel}. `
            + `The version on this screen has ${itemCountLabel(confirming.localItems)}, ${confirming.localTotalLabel}. `
            + 'Replacing discards the saved version\'s changes.'
        : problem
            ? problem
            : reviewing
                ? onOverwrite
                    ? 'Reload uses the newer saved version. Replace saved version uses your edits from this screen.'
                    : 'Reload uses the newer saved version and discards edits on this screen. Keep editing leaves your work unchanged.'
                : saveRefused
                    ? 'Nothing was overwritten. Your work is safe on this screen.'
                    : hasLocalEdits
                        ? 'Your work is safe. Review when ready.'
                        : 'A newer saved version is ready.';

    return (
        <aside
            className={`qm-project-update-notice${reviewing ? ' qm-project-update-notice--reviewing' : ''}${saveRefused ? ' qm-project-update-notice--save-refused' : ''}`}
            role="status"
            aria-live="polite"
            aria-atomic="true"
            data-local-edits={hasLocalEdits ? 'true' : 'false'}
            data-save-refused={saveRefused ? 'true' : 'false'}
        >
            <span className="qm-project-update-notice__signal" aria-hidden="true">
                <Icons.RefreshCw size={15} />
            </span>
            <div className="qm-project-update-notice__copy">
                <strong>{title}</strong>
                <span>{body}</span>
            </div>

            <div className="qm-project-update-notice__actions">
                {confirming ? (
                    <>
                        <button
                            type="button"
                            className="qm-project-update-notice__secondary qm-project-update-notice__secondary--replace"
                            onClick={overwrite}
                            disabled={resolving !== null}
                        >
                            {resolving === 'overwrite' ? 'Replacing…' : 'Replace it'}
                        </button>
                        <button
                            type="button"
                            className="qm-project-update-notice__action"
                            onClick={() => setConfirming(null)}
                            disabled={resolving !== null}
                        >
                            Cancel
                        </button>
                    </>
                ) : (
                    <>
                        {needsReview && !reviewing ? (
                            <button
                                type="button"
                                className="qm-project-update-notice__action"
                                onClick={() => setReviewing(true)}
                            >
                                Review
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="qm-project-update-notice__action"
                                onClick={reload}
                                disabled={resolving !== null}
                            >
                                {resolving === 'reload'
                                    ? 'Reloading…'
                                    : reviewing
                                        ? 'Reload saved version'
                                        : 'Reload'}
                            </button>
                        )}

                        {reviewing ? (
                            onOverwrite ? (
                                <button
                                    type="button"
                                    className="qm-project-update-notice__secondary qm-project-update-notice__secondary--replace"
                                    onClick={reviewOverwrite}
                                    disabled={resolving !== null}
                                >
                                    {resolving === 'describe' ? 'Reading the saved version…' : 'Replace saved version'}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="qm-project-update-notice__secondary"
                                    onClick={() => setReviewing(false)}
                                >
                                    Keep editing
                                </button>
                            )
                        ) : null}
                    </>
                )}
            </div>

            <button
                type="button"
                className="qm-project-update-notice__dismiss"
                onClick={onDismiss}
                aria-label="Dismiss update notice"
                title="Dismiss update notice"
            >
                <Icons.X size={15} />
            </button>
        </aside>
    );
}
